<script setup>
/**
 * AlertsStickyShowcase v5 — three rotating tiles with a notification
 * "arrival" wiggle, instead of a static 3×3 grid.
 *
 * Behaviour:
 *  - Three tiles visible at any moment, drawn from the pool of 7
 *    alert types via a circular offset.
 *  - Every 3.5s the offset advances by one: leftmost tile fades + slides
 *    out, middle/right shift left, a new tile slides in from the right
 *    with a subtle overshoot + 2 micro-shakes to evoke a notification
 *    settling into place.
 *  - On screens narrower than lg, the rotation is dropped and all 7
 *    tiles are shown stacked (horizontal carousel doesn't make sense
 *    in single-column mobile).
 *  - Tile design from the previous iteration is preserved: coloured
 *    top stripe, icon-stamp with halo, body prose, italic concrete
 *    example, chip-style channel footer.
 *
 * The CTA ("Ver cómo se configuran") that used to be an 8th tile
 * comes back below the carousel as a tail link — keeping it out of
 * rotation keeps the carousel narrative purely about alert types.
 */
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import {
  Clock, Globe, AppWindow, MapPin, TrendingDown, BarChart3, Activity,
  ArrowRight, Mail, MessageSquare, LayoutGrid,
} from "lucide-vue-next";
import AnimatedSection from "@/components/AnimatedSection.vue";

const { t } = useI18n();

const CHANNEL_META = {
  email: { icon: Mail, label: "email" },
  panel: { icon: LayoutGrid, label: "panel" },
  slack: { icon: MessageSquare, label: "Slack" },
  teams: { icon: MessageSquare, label: "Teams" },
};

const alerts = computed(() => [
  {
    id: "inactivity",
    icon: Clock,
    accent: "amber",
    channels: ["email", "panel", "slack"],
    title: t("landing.alerts_showcase.items.inactivity.title"),
    body: t("landing.alerts_showcase.items.inactivity.body"),
    subject: t("landing.alerts_showcase.items.inactivity.subject"),
  },
  {
    id: "app",
    icon: AppWindow,
    accent: "cyan",
    channels: ["panel", "email"],
    title: t("landing.alerts_showcase.items.app.title"),
    body: t("landing.alerts_showcase.items.app.body"),
    subject: t("landing.alerts_showcase.items.app.subject"),
  },
  {
    id: "web",
    icon: Globe,
    accent: "cyan",
    channels: ["panel"],
    title: t("landing.alerts_showcase.items.web.title"),
    body: t("landing.alerts_showcase.items.web.body"),
    subject: t("landing.alerts_showcase.items.web.subject"),
  },
  {
    id: "location",
    icon: MapPin,
    accent: "violet",
    channels: ["email"],
    title: t("landing.alerts_showcase.items.location.title"),
    body: t("landing.alerts_showcase.items.location.body"),
    subject: t("landing.alerts_showcase.items.location.subject"),
  },
  {
    id: "low_prod",
    icon: TrendingDown,
    accent: "rose",
    channels: ["email", "slack"],
    title: t("landing.alerts_showcase.items.low_prod.title"),
    body: t("landing.alerts_showcase.items.low_prod.body"),
    subject: t("landing.alerts_showcase.items.low_prod.subject"),
  },
  {
    id: "category",
    icon: BarChart3,
    accent: "orange",
    channels: ["email", "panel"],
    title: t("landing.alerts_showcase.items.category.title"),
    body: t("landing.alerts_showcase.items.category.body"),
    subject: t("landing.alerts_showcase.items.category.subject"),
  },
  {
    id: "low_util",
    icon: Activity,
    accent: "amber",
    channels: ["email", "slack"],
    title: t("landing.alerts_showcase.items.low_util.title"),
    body: t("landing.alerts_showcase.items.low_util.body"),
    subject: t("landing.alerts_showcase.items.low_util.subject"),
  },
]);

const ACCENT = {
  amber:  { text: "text-amber-600 dark:text-amber-400",   stripe: "bg-amber-500/85",  iconHsl: "38 92% 50%"  },
  cyan:   { text: "text-cyan-600 dark:text-cyan-400",     stripe: "bg-cyan-500/85",   iconHsl: "190 90% 45%" },
  violet: { text: "text-violet-600 dark:text-violet-400", stripe: "bg-violet-500/85", iconHsl: "262 70% 58%" },
  rose:   { text: "text-rose-600 dark:text-rose-400",     stripe: "bg-rose-500/85",   iconHsl: "350 80% 58%" },
  orange: { text: "text-orange-600 dark:text-orange-400", stripe: "bg-orange-500/85", iconHsl: "24 90% 55%"  },
};

const iconStampStyle = (accent) => {
  const hsl = ACCENT[accent].iconHsl;
  return {
    background: `linear-gradient(135deg, hsl(${hsl} / 0.28) 0%, hsl(${hsl} / 0.12) 100%)`,
    border: `1px solid hsl(${hsl} / 0.42)`,
    boxShadow: `inset 0 1px 0 hsl(0 0% 100% / 0.35), 0 0 18px hsl(${hsl} / 0.20), 0 1px 2px hsl(${hsl} / 0.15)`,
    color: `hsl(${hsl})`,
  };
};

// === Carousel state ===
// VISIBLE is responsive: 1 tile on mobile (the section is one column
// tall and a 3-up row would be unreadable), 3 tiles on lg+. The same
// notification arrival/leave animations and progress strip work in
// both layouts; only the grid template + tile count differ.
const ROTATE_MS = 3500;
const offset = ref(0);
const isLg = ref(false);
const VISIBLE_COUNT = computed(() => (isLg.value ? 3 : 1));

const visibleAlerts = computed(() => {
  const list = alerts.value;
  if (list.length === 0) return [];
  return Array.from({ length: VISIBLE_COUNT.value }, (_, i) =>
    list[(offset.value + i) % list.length]
  );
});

let timer;
let mql;
let mqlHandler;
onMounted(() => {
  // Track lg breakpoint reactively so visible count adapts to resize
  // (and orientation changes on tablets). Tailwind's lg breakpoint is
  // 1024px by default.
  mql = window.matchMedia("(min-width: 1024px)");
  isLg.value = mql.matches;
  mqlHandler = (e) => {
    isLg.value = e.matches;
  };
  mql.addEventListener("change", mqlHandler);

  timer = setInterval(() => {
    offset.value = (offset.value + 1) % alerts.value.length;
  }, ROTATE_MS);
});
onUnmounted(() => {
  if (timer) clearInterval(timer);
  if (mql && mqlHandler) mql.removeEventListener("change", mqlHandler);
});

// FLIP-style position capture so a leaving tile animates out from its
// real visible spot, not from the parent's top-left. Required when the
// transition class uses `position: absolute` to take the tile out of
// flow during leave.
const onBeforeLeave = (el) => {
  const rect = el.getBoundingClientRect();
  const parentRect = el.parentElement.getBoundingClientRect();
  el.style.position = "absolute";
  el.style.left = `${rect.left - parentRect.left}px`;
  el.style.top = `${rect.top - parentRect.top}px`;
  el.style.width = `${rect.width}px`;
  el.style.height = `${rect.height}px`;
};

// Subtle arc — per-slot rotation + vertical offset so the row of three
// tiles isn't perfectly aligned. Middle tile lifts slightly, side tiles
// drop slightly and tilt outward. Numbers kept low ("muy sutil") so the
// effect reads as physicality rather than decoration.
const ARC_ROTS = [-0.6, 0, 0.6]; // degrees per slot index
const ARC_TYS  = [2, -3, 2];     // pixels per slot index
</script>

<template>
  <section class="py-24 bg-secondary/40">
    <div class="container mx-auto px-6">
      <AnimatedSection animation="fade-up" class="text-center mb-14 max-w-2xl mx-auto">
        <p class="text-xs font-semibold uppercase tracking-widest text-accent mb-3">
          {{ t("landing.alerts_showcase.eyebrow") }}
        </p>
        <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
          {{ t("landing.alerts_showcase.title") }}
        </h2>
        <p class="text-lg text-muted-foreground">
          {{ t("landing.alerts_showcase.subtitle") }}
        </p>
      </AnimatedSection>

      <!-- Responsive carousel: 1 tile visible on mobile, 3 on lg+.
           The outer overflow-hidden wrapper clips entering/leaving
           tiles so they actually appear/disappear at the section's
           edges rather than fading inside the layout. py-4 leaves
           vertical room for the cards' drop shadows. Grid columns
           and tile count both flip at the lg breakpoint. -->
      <div class="max-w-6xl mx-auto">
        <div class="overflow-hidden py-4">
          <TransitionGroup
            name="notify"
            tag="div"
            class="relative grid grid-cols-1 lg:grid-cols-3 gap-5"
            @before-leave="onBeforeLeave"
          >
            <!-- Wrapper handles enter/leave/move animations. Inside,
                 the article carries the slot-arc transform via CSS
                 vars. Arc only applies on lg (3-tile layout); on
                 mobile a single centered tile doesn't need symmetry-
                 breaking, so the vars default to 0. -->
            <div
              v-for="(alert, idx) in visibleAlerts"
              :key="alert.id"
              class="arc-slot"
              :style="{
                '--slot-rot': isLg ? `${ARC_ROTS[idx]}deg` : '0deg',
                '--slot-ty':  isLg ? `${ARC_TYS[idx]}px`  : '0px',
              }"
            >
              <article class="relative h-full rounded-xl card-elevated-premium overflow-hidden flex flex-col">
                <!-- Top accent stripe -->
                <div class="h-[3px] w-full" :class="ACCENT[alert.accent].stripe" />

                <div class="p-5 lg:p-6 flex flex-col flex-1">
                  <div class="flex items-start justify-between mb-4">
                    <div
                      class="inline-flex items-center justify-center w-11 h-11 rounded-xl"
                      :style="iconStampStyle(alert.accent)"
                    >
                      <component :is="alert.icon" class="w-5 h-5" />
                    </div>
                    <!-- Position indicator: global index of this tile within the 7-alert pool -->
                    <span class="text-[10px] font-mono uppercase tracking-wider text-muted-foreground tabular-nums pt-1">
                      {{ String((offset + idx) % alerts.length + 1).padStart(2, "0") }} / 07
                    </span>
                  </div>

                  <h3 class="text-base lg:text-lg font-bold text-foreground mb-2 tracking-tight leading-snug">
                    {{ alert.title }}
                  </h3>

                  <p class="text-sm text-muted-foreground leading-relaxed">
                    {{ alert.body }}
                  </p>

                  <p class="mt-3 text-xs italic text-muted-foreground/70 leading-snug">
                    <span class="font-mono not-italic uppercase tracking-wider text-[10px] mr-1">
                      {{ t("landing.alerts_showcase.example_label") }}
                    </span>
                    <span :class="ACCENT[alert.accent].text" class="not-italic font-medium">{{ alert.subject }}</span>
                  </p>

                  <div class="flex flex-wrap items-center gap-1.5 mt-auto pt-4">
                    <span
                      v-for="ch in alert.channels"
                      :key="ch"
                      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-border bg-muted/40 text-[10px] font-medium text-foreground/85"
                    >
                      <component :is="CHANNEL_META[ch].icon" class="w-2.5 h-2.5 text-muted-foreground" />
                      <span>{{ CHANNEL_META[ch].label }}</span>
                    </span>
                  </div>
                </div>
              </article>
            </div>
          </TransitionGroup>
        </div>

        <!-- Progress strip: 7 dots, current `offset` is highlighted.
             Same on mobile and desktop. -->
        <div class="flex justify-center gap-1.5 mt-6">
          <span
            v-for="(_, i) in alerts"
            :key="i"
            :class="[
              'h-1 rounded-full transition-all duration-300',
              i === offset ? 'w-6 bg-accent' : 'w-1 bg-muted-foreground/30',
            ]"
          />
        </div>
      </div>

      <!-- Tail CTA below the carousel (both layouts) -->
      <AnimatedSection animation="fade-up" :delay="200" class="text-center mt-10">
        <a
          href="#contact-form"
          class="inline-flex items-center gap-1.5 text-sm text-accent hover:text-foreground transition-colors font-medium"
        >
          {{ t("landing.alerts_showcase.tail_link") }}
          <ArrowRight class="w-3.5 h-3.5" />
        </a>
      </AnimatedSection>
    </div>
  </section>
</template>

<style scoped>
/* Per-slot arc: subtle tilt + vertical offset on the inner article so
 * the row of three tiles doesn't form a perfectly straight line.
 * Transforms compose with the wrapper's enter/leave/move animations
 * because they live on different elements. The transition makes the
 * arc smoothly re-settle when a tile shifts slot (e.g. from rightmost
 * to middle as it survives a rotation). */
.arc-slot > article {
  transform: rotateZ(var(--slot-rot, 0deg)) translateY(var(--slot-ty, 0px));
  transition: transform 600ms cubic-bezier(0.4, 0, 0.2, 1);
}

/* Notification "arrival" — slides in from beyond the right edge
 * (translateX 110% of slot width, clipped by the parent's overflow-
 * hidden), then two tiny lateral shakes as it settles, evoking a
 * toast notification with haptic-feedback vibration. */
@keyframes notify-arrive {
  0%   { opacity: 0; transform: translateX(110%) scale(0.96); }
  55%  { opacity: 1; transform: translateX(0) scale(1.03); }
  70%  { transform: translateX(-4px) scale(1); }
  82%  { transform: translateX(3px); }
  92%  { transform: translateX(-1px); }
  100% { opacity: 1; transform: translateX(0); }
}

.notify-enter-active {
  animation: notify-arrive 780ms cubic-bezier(0.34, 1.4, 0.64, 1) both;
}

/* Leaving tile slides off through the left edge and fades.
 * position:absolute (set inline by onBeforeLeave) takes it out of
 * flow so the remaining two tiles shift cleanly. */
.notify-leave-active {
  transition: opacity 500ms ease, transform 500ms cubic-bezier(0.4, 0, 0.2, 1);
}
.notify-leave-to {
  opacity: 0;
  transform: translateX(-110%) scale(0.96);
}

/* The two tiles that survive a rotation: animate from their old grid
 * cell to the new one via TransitionGroup's FLIP. */
.notify-move {
  transition: transform 600ms cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
